/**
 * OmegaCanon — iter 144
 * Implements the Capability Canon PDF's user-facing pieces:
 *   • <DepthExplainer>     — Headline / Brief / Teardown toggle (real Claude)
 *   • <LinkageMapCard>     — directed cross-asset chain for a symbol
 *   • <ConfidencePill>     — HIGH / MEDIUM / LOW / THIN_DATA from real data
 *   • <GlossaryPopover>    — clickable definitions
 *   • <KnownLimitsCard>    — Honesty Clause + 5 risks verbatim from canon
 *   • <GrowthArcCard>      — A_n = A_0 (1+g)^n from real closed trades
 * No mocks. All endpoints are real.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { fastGet } from "../lib/fastFetch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── ConfidencePill ──────────────────────────────────────────────────────────
const CONF_TONE = {
  HIGH: { bg: "rgba(74,222,128,0.12)", color: "#4ade80", border: "rgba(74,222,128,0.4)" },
  MEDIUM: { bg: "rgba(251,191,36,0.12)", color: "#fbbf24", border: "rgba(251,191,36,0.4)" },
  LOW: { bg: "rgba(248,113,113,0.12)", color: "#f87171", border: "rgba(248,113,113,0.4)" },
  THIN_DATA: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8", border: "rgba(148,163,184,0.4)" },
};

export function ConfidencePill({ symbol, compact = false }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    const url = symbol ? `${API}/omega/confidence?symbol=${symbol}` : `${API}/omega/confidence`;
    return fastGet(url, setC, () => setC(null));
  }, [symbol]);
  if (!c) return null;
  const tone = CONF_TONE[c.level] || CONF_TONE.THIN_DATA;
  return (
    <span data-testid={`confidence-pill-${c.level.toLowerCase()}`} title={c.reason} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: compact ? "2px 6px" : "3px 8px", borderRadius: 4,
      fontSize: compact ? 8 : 9, fontWeight: 800, letterSpacing: 1.2,
      background: tone.bg, color: tone.color, border: `1px solid ${tone.border}`,
      whiteSpace: "nowrap",
    }}>● {c.level.replace("_", "·")}</span>
  );
}

// ─── DepthExplainer ──────────────────────────────────────────────────────────
const DEPTHS = ["headline", "brief", "teardown"];

export function DepthExplainer({ topic, symbol, defaultDepth = "brief" }) {
  const [depth, setDepth] = useState(defaultDepth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ topic, depth: d, ...(symbol ? { symbol } : {}) });
      const r = await axios.get(`${API}/omega/explain?${q}`, { timeout: 35000 });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [topic, symbol]);

  useEffect(() => { load(depth); }, [load, depth]);

  return (
    <div className="card" data-testid="depth-explainer" style={{ borderColor: "rgba(167,139,250,0.28)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <div className="sh" style={{ margin: 0 }}>Omega · {topic}</div>
        <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 999, background: "var(--layer2, rgba(255,255,255,0.04))", border: "1px solid var(--border, rgba(255,255,255,0.08))" }}>
          {DEPTHS.map(d => (
            <button
              key={d}
              data-testid={`depth-btn-${d}`}
              onClick={() => setDepth(d)}
              style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase",
                padding: "5px 10px", borderRadius: 999,
                background: depth === d ? "#a78bfa" : "transparent",
                color: depth === d ? "#fff" : "var(--muted)",
                border: "none", cursor: "pointer", fontFamily: "inherit",
              }}
            >{d}</button>
          ))}
        </div>
      </div>
      {loading && <div style={{ fontSize: 11.5, color: "var(--muted)", padding: 6 }}>Omega is thinking…</div>}
      {error && <div style={{ fontSize: 11.5, color: "#f87171", padding: 6 }}>{error}</div>}
      {!loading && !error && data?.text && (
        <div style={{ fontSize: depth === "headline" ? 14 : 12.5, lineHeight: 1.65, color: "var(--text)", whiteSpace: "pre-wrap", fontWeight: depth === "headline" ? 600 : 400 }}>
          {data.text}
        </div>
      )}
      {data?.fromCache && (
        <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 8, letterSpacing: 0.4 }}>served from cache · {data.model || ""}</div>
      )}
    </div>
  );
}

// ─── LinkageMapCard ──────────────────────────────────────────────────────────
const DIR_COLOR = { positive: "#4ade80", negative: "#f87171" };

export function LinkageMapCard({ symbol }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true); setErr(null);
    axios.get(`${API}/omega/linkage-map?symbol=${symbol}`, { timeout: 35000 })
      .then(r => setD(r.data))
      .catch(e => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [symbol]);

  if (!symbol) return null;

  return (
    <div className="card" data-testid="linkage-map" style={{ borderColor: "rgba(167,139,250,0.28)" }}>
      <div className="sh" style={{ margin: 0 }}>Linkage Map · {symbol}</div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        Real cross-asset drivers · how the world moves this name
      </div>
      {loading && <div style={{ fontSize: 11.5, color: "var(--muted)", padding: 6 }}>Mapping drivers…</div>}
      {err && <div style={{ fontSize: 11.5, color: "#f87171", padding: 6 }}>{err}</div>}
      {d?.links?.length > 0 && (
        <div style={{ display: "grid", gap: 8 }}>
          {d.links.map((l, i) => (
            <div key={i} data-testid={`linkage-${i}`} style={{
              padding: 10, borderRadius: 10,
              background: "var(--layer1, rgba(255,255,255,0.025))",
              border: "1px solid var(--border, rgba(255,255,255,0.06))",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, fontSize: 12, fontWeight: 700, color: "var(--text)" }}>
                <span>{l.driver}</span>
                {l.driverSymbol && <span style={{ fontSize: 9, color: "var(--muted)", padding: "2px 5px", borderRadius: 3, background: "var(--layer2)" }}>{l.driverSymbol}</span>}
                <span style={{ color: DIR_COLOR[l.direction] || "var(--muted)", fontSize: 14 }}>{l.direction === "positive" ? "↗" : "↘"}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{symbol}</span>
                <span style={{ marginLeft: "auto", fontSize: 8, letterSpacing: 1, padding: "2px 6px", borderRadius: 4, background: "var(--layer2)", color: "var(--muted)", textTransform: "uppercase", fontWeight: 700 }}>{l.confidence} · {l.horizon}</span>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--dim)", lineHeight: 1.55 }}>{l.mechanism}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── GlossaryPopover (inline) ────────────────────────────────────────────────
// (fastFetch handles caching/dedup/SWR — no need for a local cache var.)

export function GlossaryCard() {
  const [terms, setTerms] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => fastGet(`${API}/omega/glossary`, (data) => setTerms(data?.terms || [])), []);
  const filtered = q ? terms.filter(t => `${t.term} ${t.long} ${t.def}`.toLowerCase().includes(q.toLowerCase())) : terms;
  return (
    <div className="card" data-testid="glossary-card" style={{ borderColor: "rgba(167,139,250,0.22)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div className="sh" style={{ margin: 0 }}>Omega Glossary</div>
        <input
          data-testid="glossary-search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search definitions…"
          style={{
            padding: "6px 10px", borderRadius: 999, fontSize: 11, fontFamily: "inherit",
            background: "var(--layer1, rgba(255,255,255,0.04))",
            border: "1px solid var(--border, rgba(255,255,255,0.1))",
            color: "var(--text)", outline: "none", minWidth: 180,
          }}
        />
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {filtered.map((t, i) => (
          <div key={t.term} data-testid={`gloss-${i}`} style={{ padding: 10, borderRadius: 10, background: "var(--layer1, rgba(255,255,255,0.025))", border: "1px solid var(--border, rgba(255,255,255,0.06))" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#a78bfa" }}>{t.term}</span>
              {t.long && t.long !== t.term && <span style={{ fontSize: 10, color: "var(--muted)" }}>{t.long}</span>}
            </div>
            <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.55 }}>{t.def}</div>
          </div>
        ))}
        {filtered.length === 0 && q && <div style={{ fontSize: 11, color: "var(--muted)", padding: 6 }}>No match for "{q}"</div>}
      </div>
    </div>
  );
}

// ─── KnownLimitsCard ─────────────────────────────────────────────────────────
const LIMIT_TONE = {
  "Model Risk": "#f59e0b", "Data Risk": "#fbbf24",
  "Tail Risk": "#f87171", "Execution Risk": "#94a3b8", "Overconfidence Risk": "#a78bfa",
};

export function KnownLimitsCard() {
  const [d, setD] = useState(null);
  useEffect(() => fastGet(`${API}/omega/known-limits`, setD), []);
  if (!d) return null;
  return (
    <div className="card" data-testid="known-limits" style={{ borderColor: "rgba(248,113,113,0.22)" }}>
      <div className="sh" style={{ margin: 0 }}>The Honesty Clause</div>
      <div style={{ fontSize: 11.5, color: "var(--dim)", lineHeight: 1.65, marginTop: 6, marginBottom: 12, padding: 10, borderRadius: 8, background: "rgba(248,113,113,0.05)", border: "1px solid rgba(248,113,113,0.18)" }}>
        {d.honestyClause}
      </div>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: "var(--muted)", marginBottom: 8 }}>KNOWN LIMITS</div>
      <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
        {(d.limits || []).map((l, i) => (
          <div key={i} data-testid={`limit-${i}`} style={{ padding: 10, borderRadius: 10, background: "var(--layer1, rgba(255,255,255,0.025))", border: "1px solid var(--border, rgba(255,255,255,0.06))" }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: LIMIT_TONE[l.kind] || "var(--text)", marginBottom: 4 }}>{l.kind}</div>
            <div style={{ fontSize: 11.5, color: "var(--text)", lineHeight: 1.55, marginBottom: 4 }}>{l.summary}</div>
            <div style={{ fontSize: 10.5, color: "var(--muted)", fontStyle: "italic", lineHeight: 1.5 }}>Mitigation: {l.mitigation}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: "var(--muted)", marginBottom: 8 }}>THE OMEGA DOCTRINE</div>
      <ol style={{ paddingLeft: 18, fontSize: 11.5, color: "var(--text)", lineHeight: 1.7, margin: 0 }}>
        {(d.doctrine || []).map((rule, i) => <li key={i}>{rule}</li>)}
      </ol>
    </div>
  );
}

// ─── GrowthArcCard ───────────────────────────────────────────────────────────
function fmt$(n) { return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }

export function GrowthArcCard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => fastGet(
    `${API}/omega/growth-arc`,
    (v) => { setD(v); setLoading(false); },
    (e) => { setErr(e.response?.data?.detail || e.message); setLoading(false); },
  ), []);

  const projection = d?.projection || [];
  const minEq = Math.min(...projection.map(p => p.equity), d?.a0 || 0);
  const maxEq = Math.max(...projection.map(p => p.equity), d?.a0 || 1);
  const range = maxEq - minEq || 1;

  return (
    <div className="card" data-testid="growth-arc" style={{ borderColor: "rgba(167,139,250,0.22)" }}>
      <div className="sh" style={{ margin: 0 }}>Capital Growth Arc</div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10, fontFamily: "monospace" }}>
        {d?.formula || "A_n = A_0 (1+g)^n"}
      </div>
      {loading && <div style={{ textAlign: "center", padding: "16px 0", color: "var(--dim)", fontSize: 12 }}><span className="spin" style={{ marginRight: 6 }} />Computing growth arc…</div>}
      {err && <div style={{ fontSize: 11, color: "#f87171" }}>{err}</div>}
      {d && !loading && (
        <>
          {d.g_sample_count === 0 ? (
            <div style={{ textAlign: "center", padding: "12px 0", color: "var(--dim)", fontSize: 12 }}>No closed trades yet — execute trades to build the growth arc.</div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, marginBottom: 12 }}>
                <div><div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>A₀ now</div><div style={{ fontSize: 14, fontWeight: 800 }}>{fmt$(d.a0)}</div></div>
                <div><div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>g / trade</div><div style={{ fontSize: 14, fontWeight: 800, color: d.g_per_trade >= 0 ? "#4ade80" : "#f87171" }}>{(d.g_per_trade * 100).toFixed(2)}%</div></div>
                <div><div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>samples</div><div style={{ fontSize: 14, fontWeight: 800 }}>{d.g_sample_count}</div></div>
                <div><div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>A₂₀ proj</div><div style={{ fontSize: 14, fontWeight: 800, color: "#a78bfa" }}>{fmt$(projection[20]?.equity || d.a0)}</div></div>
              </div>
              {/* Sparkline with gradient fill */}
              <div style={{ position: "relative", height: 64, marginBottom: 10, background: "var(--layer1, rgba(255,255,255,0.025))", borderRadius: 8, padding: 6, border: "1px solid var(--border, rgba(255,255,255,0.06))" }}>
                <svg width="100%" height="100%" viewBox={`0 0 ${Math.max(projection.length - 1, 1)} 100`} preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="arcGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.01" />
                    </linearGradient>
                  </defs>
                  <polygon
                    fill="url(#arcGrad)"
                    points={[
                      "0,100",
                      ...projection.map((p, i) => `${i},${100 - ((p.equity - minEq) / range) * 90}`),
                      `${projection.length - 1},100`
                    ].join(" ")}
                  />
                  <polyline
                    fill="none" stroke="#a78bfa" strokeWidth="1.8"
                    points={projection.map((p, i) => `${i},${100 - ((p.equity - minEq) / range) * 90}`).join(" ")}
                  />
                </svg>
              </div>
            </>
          )}
          <div style={{ fontSize: 10.5, color: "var(--muted)", lineHeight: 1.55, fontStyle: "italic" }}>
            {d.disclaimer}
          </div>
        </>
      )}
    </div>
  );
}
